import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Text,
  SimpleGrid,
  Button,
  useToast,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Link,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faYelp,
  faGoogle, // you'll need to install @fortawesome/free-brands-svg-icons if not already
} from "@fortawesome/free-brands-svg-icons";

// Custom icons for RateMds, HealthGrades, etc. (we'll treat them as image URLs)
const customIcons = {
  rate_mds: "https://prod-viva-assets.s3.us-west-2.amazonaws.com/favicon-rate_mds.png",
  health_grades: "https://prod-viva-assets.s3.us-west-2.amazonaws.com/favicon-health_grades.ico",
};

// Color mapping for each rating from 1 to 10
const ratingColors = [
  "#C44C3B",
  "#D8592D",
  "#ED9230",
  "#EBC030",
  "#F1CD3B",
  "#55B83A",
  "#3C9420",
  "#2B45C0",
  "#6830E6",
  "#9E30F6",
];

import { cqAPI } from "../../lib/Auth";

export default function RateAppointment() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [info, setInfo] = useState({});
  const [selectedRating, setSelectedRating] = useState(null);

  // Whether to display the "review sites" or a simple "thank you"
  const [showReviewSites, setShowReviewSites] = useState(false);

  // Extract query parameters
  const bid = searchParams.get("bid");
  const cid = searchParams.get("cid");
  const aid = searchParams.get("aid");

  // --- FETCH APPOINTMENT DATA ---
  const getAppointmentData = async () => {
    try {
      const response = await cqAPI.get(`/rateappt-info?bid=${bid}&cid=${cid}&aid=${aid}`);
      if (response.type === "error") {
        toast({
          description: response.msg || "Something went wrong.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setInfo(response.data);
    } catch (error) {
      toast({
        description: "Unable to fetch appointment details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // --- HANDLE RATING ---
  const handleRating = async (ratingValue) => {
    setSelectedRating(ratingValue);
    try {
      const response = await cqAPI.post("/rateappt", {
        bid,
        cid,
        aid,
        rating: ratingValue,
      });
      if (response.type === "ok") {
        toast({
          description: "Thanks for sending your feedback!",
          status: "success",
          duration: 1200,
          isClosable: true,
        });
        // Compare rating with nps
        if (ratingValue >= info.nps) {
          // Show the "review sites" modal
          setShowReviewSites(true);
        } else {
          // Show the "thank you" modal
          setShowReviewSites(false);
        }
        // Finally, open the modal (cannot close on overlay/ESC if below nps)
        onOpen();
      } else {
        toast({
          description: response.msg || "Something went wrong.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        description: "Submission failed. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Identify which sites are supported
  const buildReviewLinks = () => {
    if (!info.social) return [];

    const links = [];
    // For each known site, if present, push a link configuration
    if (info.social.google) {
      // e.g. Google place_id => https://search.google.com/local/writereview?placeid=PLACE_ID
      links.push({
        site: "google",
        label: "Google",
        href: `https://search.google.com/local/writereview?placeid=${info.social.google}`,
        icon: faGoogle,
      });
    }
    if (info.social.facebook) {
      links.push({
        site: "facebook",
        label: "Facebook",
        href: `https://www.facebook.com/${info.social.facebook}`,
        icon: faFacebook,
      });
    }
    if (info.social.yelp) {
      links.push({
        site: "yelp",
        label: "Yelp",
        href: `https://www.yelp.com/biz/${info.social.yelp}`,
        icon: faYelp,
      });
    }
    if (info.social.rate_mds) {
      links.push({
        site: "rate_mds",
        label: "RateMDs",
        href: `https://www.ratemds.com/${info.social.rate_mds}`,
        icon: customIcons.rate_mds, // We'll render as an <img />
      });
    }
    if (info.social.health_grades) {
      links.push({
        site: "health_grades",
        label: "HealthGrades",
        href: `https://www.healthgrades.com/${info.social.health_grades}`,
        icon: customIcons.health_grades,
      });
    }

    return links;
  };

  useEffect(() => {
    getAppointmentData();
  }, []);
  
  if (!info.starttime){
    return (<Box />)
  }

  return (
    <Box
      maxW="500px"
      mx="auto"
      mt={10}
      p={6}
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      textAlign="center"
    >
      <Heading as="h1" size="lg" mb={3}>
        We Value Your Feedback
      </Heading>

      <Text fontSize="sm" mb={6} color="gray.600">
        {info.starttime ? (
          <>
            Thank you for visiting{" "}
            <Text as="span" fontWeight="semibold">
              {info.business}
            </Text>{" "}
            on {info.starttime}. We greatly appreciate you taking the time to
            rate your experience. Your feedback helps us improve and maintain
            the highest level of care for all our patients.
          </>
        ) : (
          "Thank you for choosing our practice. We greatly appreciate you taking the time to rate your experience."
        )}
      </Text>

      <Text fontSize="md" fontWeight="medium" mb={6}>
        Please rate your visit from 1 (Very Poor) to 10 (Excellent).
      </Text>

      <SimpleGrid columns={{ base: 5, md: 5 }} spacing={3}>
        {[...Array(10)].map((_, index) => {
          const ratingNumber = index + 1; // 1..10
          const color = ratingColors[index];
          return (
            <Button
              key={ratingNumber}
              bg={color}
              color="white"
              size="lg"
              _hover={{ opacity: 0.85 }}
              onClick={() => handleRating(ratingNumber)}
            >
              {ratingNumber}
            </Button>
          );
        })}
      </SimpleGrid>

      {/* MODAL */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        // For the "thank you" scenario, we can disable clicks.
        // If showReviewSites=false => user can't close on overlay or ESC
        closeOnEsc={showReviewSites} 
        closeOnOverlayClick={showReviewSites}
      >
        <ModalOverlay />
        <ModalContent>
          {showReviewSites ? (
            <>
              <ModalHeader>Thank you for your rating!</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text mb={4}>
                  We’re so glad you had a positive experience. Would you mind
                  leaving a quick review on one of these sites?
                </Text>
                <VStack align="flex-start" spacing={4}>
                  {buildReviewLinks().map((item) => (
                    <HStack key={item.site} spacing={3}>
                      {typeof item.icon === "string" ? (
                        // custom icon as an <img />
                        <Box boxSize="20px">
                          <img
                            src={item.icon}
                            alt={item.label}
                            style={{ width: "100%", height: "auto" }}
                          />
                        </Box>
                      ) : (
                        // brand icon via FontAwesome
                        <Icon as={FontAwesomeIcon} icon={item.icon} w={5} h={5} />
                      )}
                      <Link
                        href={item.href}
                        color="blue.500"
                        fontWeight="semibold"
                        target="_blank"
                      >
                        Review on {item.label}
                      </Link>
                    </HStack>
                  ))}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onClose} colorScheme="blue">
                  Close
                </Button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalHeader>Thank you!</ModalHeader>
              {/* Hide close button to fully disable closing */}
              {!showReviewSites ? null : <ModalCloseButton />}
              <ModalBody>
                <Text>
                  We appreciate your feedback and will use it to improve our
                  services.
                </Text>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
}
